import type { DraftTaskAttachmentList } from '@frontend/api-client';
import { FILE_INPUT_COUNT_MAX_FILES, getScopeFailureExplanationCopy } from '@frontend/api-client';
import { type StrictOmit } from '@frontend/duck-tape';
import type { DraftParentStepProps } from '@frontend/react-domain';
import { useCreateDraftTask, useDescriptionStep } from '@frontend/react-domain';
import { useDismissedSuggestionsSlice } from '@frontend/redux';
import type { TypedSocket } from '@frontend/sockets';
import {
  Button,
  DragAndDropModal,
  FileInput,
  FilePreviewsList,
  InlineAlert,
  Spinner,
  Text,
  TextareaInput,
  XStack,
  YStack,
} from '@frontend/web-react';
import { getHotkeyHandler, safeCss } from '@frontend/web-utils';
import { WebTypedFormData } from '@frontend/web-utils/webTypedFormData';
import {
  useAnalytics,
  useCreateDraftTaskMutation,
  useEditDraftTaskAfterFailureMutation,
  useUpdateDraftTaskMutation,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useNavigate } from '@tanstack/react-router';
import { useRef } from 'react';
import { SuggestionList } from './Suggestions/SuggestionList';

export const Description = ({
  description,
  incrementParentStep,
  isFromSignup,
  setupSocket,
}: StrictOmit<DraftParentStepProps, 'decrementParentStep'> & {
  description?: string;
  isFromSignup?: boolean;
  setupSocket: (draftTaskId: string) => TypedSocket<'draftTaskCreation'>;
}) => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { dismissSuggestion } = useDismissedSuggestionsSlice();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isRetrieveDraftTaskLoading, manualRefetchScopeCheck, showScopeCheckStuckButton, state } =
    useCreateDraftTask();
  const {
    handleDescriptionChange,
    handleFileChoose,
    handleFileRemove,
    isAnyLoading,
    isContinueDisabled,
    onPressContinue,
    onPressImFeelingDucky,
  } = useDescriptionStep({
    TypedFormData: WebTypedFormData,
    description,
    dismissSuggestion,
    handleMutation,
    incrementParentStep,
    onCreateDraftSuccess: (draftTaskId) => {
      track({ data: { entryPoint: 'new_task_page' }, event: 'draftTaskCreated', topic: 'draftTasks' });
      navigate({ params: { draftTaskId }, replace: true, search: { source: 'new_task_page' }, to: '/app/draft-task/$draftTaskId' });
    },
    platformSource: 'task_source_web_app',
    setupSocket,
    useCreateDraftTaskMutation,
    useEditDraftTaskAfterFailureMutation,
    useUpdateDraftTaskMutation,
  });

  return (
    <YStack className="grid grid-cols-1 lg:grid-cols-4 w-full px-md">
      <YStack className="col-start-1 lg:col-start-2 lg:col-span-2">
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <YStack className="w-full h-[60vh]" justifyContent="center">
          <YStack gapY="lg" px="sm">
            <YStack className="hover:cursor-text" flex={1} onClick={() => textareaRef.current?.focus()}>
              <TextareaInput
                autoFocus
                classNames={{
                  input: safeCss('text-h1 font-normal border-none p-none bg-transparent'),
                }}
                disabled={state.isScopeCheckLoading}
                maxRows={3}
                minRows={1}
                onChange={handleDescriptionChange}
                onKeyDown={getHotkeyHandler([
                  [
                    'mod+Enter',
                    () => {
                      if (isAnyLoading || !state.description) return;
                      onPressContinue();
                    },
                  ],
                ])}
                placeholder="Describe your task..."
                ref={textareaRef}
                value={state.description || ''}
              />
            </YStack>
            {state.description ? null : (
              <YStack gapY="lg">
                <Text color="primary" type="p-md">
                  {"Duckbill's"} best at getting things done, like{' '}
                  <b>calling, booking, registering, scheduling, ordering, purchasing, calendaring.</b>
                </Text>
                <Text color="primary" type="p-sm">
                  Before you submit your task, we suggest you have an outcome in mind of what {'"done"'} looks like to
                  you!
                </Text>
              </YStack>
            )}
            {state.attachments.length ? (
              <FilePreviewsList files={state.attachments} maxItemWidth={200} onRemove={handleFileRemove} />
            ) : null}
            {state.isScopeCheckLoading ? (
              <YStack gapY="md">
                <XStack alignItems="center" gap="sm">
                  <Spinner color="neutral.9" size={14} />
                  <Text color="tertiary" type="p-sm">
                    Analyzing task
                  </Text>
                </XStack>
                {showScopeCheckStuckButton ? (
                  <XStack justifyContent="start">
                    <Button
                      className="border-2 border-borderPrimary"
                      isLoading={isRetrieveDraftTaskLoading}
                      onClick={manualRefetchScopeCheck}
                      size="sm"
                      variant="secondary"
                    >
                      Stuck loading? Click here.
                    </Button>
                  </XStack>
                ) : null}
              </YStack>
            ) : null}
            <XStack alignItems="center" flex={1} justifyContent="between">
              <FileInput
                allowedFileTypes={['image', 'pdf']}
                countMaxFiles={FILE_INPUT_COUNT_MAX_FILES}
                disabled={isAnyLoading}
                iconName="IconPaperclip"
                label="Attach"
                onChange={(val) => handleFileChoose(val as DraftTaskAttachmentList)}
                value={state.attachments}
              />
              <XStack gap="sm">
                {isFromSignup ? (
                  <Button label="Skip for now" onClick={() => navigate({ to: '/app/home' })} variant="secondary" />
                ) : null}
                <Button
                  disabled={isContinueDisabled}
                  isLoading={isAnyLoading}
                  label="Continue"
                  onClick={onPressContinue}
                  variant="primary"
                />
                {state.description ? null : (
                  <Button
                    // eslint-disable-next-line tailwindcss/no-arbitrary-value
                    className="rounded-[100px]"
                    disabled={false}
                    isLoading={isAnyLoading}
                    onClick={() => {
                      track({ event: 'imFeelingDuckySelected', topic: 'draftTasks' });
                      void onPressImFeelingDucky();
                    }}
                    variant="secondary"
                  >
                    <span aria-label="duck" role="img">
                      🦆
                    </span>{' '}
                    {"I'm Feeling Ducky"}
                  </Button>
                )}
              </XStack>
            </XStack>
            {state.scopeFailureReason ? (
              <YStack alignItems="center">
                {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                <YStack className="max-w-[75%]">
                  <InlineAlert description={getScopeFailureExplanationCopy(state.scopeFailureReason)} variant="error" />
                </YStack>
              </YStack>
            ) : null}
          </YStack>
        </YStack>
        {!state.description ? <SuggestionList onSuggestionSelect={handleDescriptionChange} /> : null}
      </YStack>
      <DragAndDropModal
        fileLimitReached={state.attachments?.length >= FILE_INPUT_COUNT_MAX_FILES}
        onFilesDropped={(attachments) => handleFileChoose(attachments as DraftTaskAttachmentList)}
      />
    </YStack>
  );
};
